import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'

export function InputWithError({
	label,
	error,
	className,
	...props
}: {
	label: string
	error: string[] | undefined
	className?: string
} & React.ComponentProps<'input'>) {
	return (
		<div className={cn('flex flex-col gap-1', className)}>
			<Label htmlFor={props.id} className="text-gray-600">
				{label}
			</Label>
			<Input
				{...props}
				aria-invalid={error && error.length > 0 ? 'true' : undefined}
				className={`h-9 ${props.type === 'number' ? 'text-right' : 'text-left'}`}
			/>
			<ErrorLabel error={error} />
		</div>
	)
}

export function ErrorLabel({ error }: { error: string[] | undefined }) {
	if (error) {
		return <span className="text-xs text-red-500">{error}</span>
	}
}

interface DynamicInputProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id'> {
	label: string
	subLabel?: string
	id?: string
	error?: string[] | undefined
}

export function DynamicInput({
	label,
	subLabel,
	id,
	type = 'text',
	error,
	className,
	...props
}: DynamicInputProps) {
	const inputId = id || label.toLowerCase().replace(/\s+/g, '-')

	return (
		<div className="w-full space-y-1">
			<div className="flex items-baseline justify-between">
				<Label
					htmlFor={inputId}
					className="text-sm text-gray-600 dark:text-gray-400"
				>
					{label}
				</Label>
				<span className="text-xs text-gray-400">{subLabel}</span>
			</div>
			<Input
				id={inputId}
				type={type}
				className={`${error ? 'border-red-500 focus-visible:ring-red-500' : ''} ${className || ''}`}
				{...props}
			/>
			{error && <p className="text-xs text-red-500">{error}</p>}
		</div>
	)
}

interface DynamicTextAreaProps
	extends Omit<React.InputHTMLAttributes<HTMLTextAreaElement>, 'id'> {
	label: string
	id?: string
	error?: string[] | undefined
}

export function DynamicTextArea({
	label,
	id,
	error,
	className,
	...props
}: DynamicTextAreaProps) {
	const inputId = id || label.toLowerCase().replace(/\s+/g, '-')

	return (
		<div className="w-full space-y-1.5">
			<Label htmlFor={inputId} className="text-sm font-medium">
				{label}
			</Label>
			<Textarea
				id={inputId}
				className={`${error ? 'border-red-500 focus-visible:ring-red-500' : ''} ${className || ''}`}
				{...props}
			/>
			{error && <p className="text-xs text-red-500">{error}</p>}
		</div>
	)
}
